import { TransformConvertorBase } from './transform.convertor';
import { Transformation } from 'app/model/dataflow/transformation';
import { DataFlowExpressionUtils } from 'app/model/adapters/dataflow/expression.util';
import { Constants } from 'app/shared/shared-constants';
import { DataFlowSchemaAdapterUtils } from '../../dataflow-schema-adapter-utils';
import { WindowTransformation } from 'app/model/dataflow/windowtransformation';
import { WindowPlainObject } from 'worker/app-workers/shared/plainObjects/window.plainObject';
import {
    ExpressionColumnPatternPlainObject,
    ExpressionColumnPlainObject
} from 'worker/app-workers/shared/plainObjects/expressionColumn.plainObject';
import { BaseSchemaModifierTransformation } from 'app/model/dataflow/base-schema-modifier-transformation';
import { IResourceContext } from 'shell-models-lib';

export class WindowConvertor extends TransformConvertorBase {
    private get windowTransform(): WindowTransformation {
        return this.transformation as WindowTransformation;
    }

    private get windowObject(): WindowPlainObject {
        return this.transformObject as WindowPlainObject;
    }

    private get transformBase(): BaseSchemaModifierTransformation {
        return this.transformation as BaseSchemaModifierTransformation;
    }

    public async convert(contextData: IResourceContext): Promise<Transformation> {
        await super.convert(contextData);
        this.windowObject.columns.forEach((column) => {
            if (column.classType === ExpressionColumnPlainObject.classType) {
                const expressionPlainObjectColumn = column as ExpressionColumnPlainObject;
                const aggColumn = expressionPlainObjectColumn.transient
                    ? this.windowTransform.addTransientColumn()
                    : this.windowTransform.addColumn();
                aggColumn.column.name = expressionPlainObjectColumn.name;
                aggColumn.dataFlowExpression.expression = DataFlowExpressionUtils.unwrapExpressionWithEmptyValue(
                    expressionPlainObjectColumn.expression
                );
                aggColumn.column.isTransient = expressionPlainObjectColumn.transient;
            } else if (column.classType === ExpressionColumnPatternPlainObject.classType) {
                DataFlowSchemaAdapterUtils.addColumnPatternToTransform(
                    this.transformBase,
                    column as ExpressionColumnPatternPlainObject
                );
            }
        });
        this.windowObject.sortColumns.forEach((column) => {
            const expression = this.windowTransform.addSortExpression();
            expression.ascending = column.ascending;
            expression.dataFlowExpression.expression = DataFlowExpressionUtils.unwrapExpressionWithEmptyValue(
                column.expression
            );
            expression.nullsFirst = column.nullsFirst;
        });
        this.windowObject.groupByColumns.forEach((column) => {
            const groupByColumn = this.windowTransform.addGroupByColumn();
            const unescapedColumnName = Constants.dataFlowEscapedNameValidation.test(column.name)
                ? DataFlowSchemaAdapterUtils.removeWrappingCharacters(column.name)
                : column.name;
            groupByColumn.column.name = column.expression
                ? unescapedColumnName
                : DataFlowSchemaAdapterUtils.extractColumnName(unescapedColumnName);
            groupByColumn.dataFlowExpression.expression = column.expression || column.name;
        });

        if (this.windowObject.configs.has('startRowOffset')) {
            this.windowTransform.range[0] = this.windowObject.configs.get('startRowOffset');
            this.windowTransform.rangeByOffset = true;
            this._handledConfigs.push('startRowOffset');
        }

        if (this.windowObject.configs.has('startRange')) {
            this.windowTransform.range[0] = this.windowObject.configs.get('startRange');
            this.windowTransform.rangeByOffset = false;
            this._handledConfigs.push('startRange');
        }

        if (this.windowObject.configs.has('endRowOffset')) {
            this.windowTransform.range[1] = this.windowObject.configs.get('endRowOffset');
            this.windowTransform.rangeByOffset = true;
            this._handledConfigs.push('endRowOffset');
        }

        if (this.windowObject.configs.has('endRange')) {
            this.windowTransform.range[1] = this.windowObject.configs.get('endRange');
            this.windowTransform.rangeByOffset = false;
            this._handledConfigs.push('endRange');
        }

        super.postConvert();
        return this.transformation;
    }
}
